/* eslint-disable camelcase */
import { createAction } from 'redux-actions';
import type { Store } from 'redux';
import { md5 } from 'js-md5';
import { Confirmation } from '@piwikpro/confirmation-crate';
import {
  NotifyOnFail,
  NotifyAboutUpdate,
} from '@piwikpro/notification-crate';
import {
  Service,
  inject,
  ConfigService,
} from '@piwikpro/platform';
import { HttpClient } from '@piwikpro/http-crate';
import {
  fetchOrganizationBrandingInitialized, fetchOrganizationBrandingSucceeded, fetchOrganizationBrandingFailed, BrandingResponse,
} from './redux/branding';
import { fetchOrganizationConfigSucceeded, fetchOrganizationConfigFailed, OrganizationConfigResponse } from './redux/config';
import {
  fetchOrganizationProductAnalyticsInitialized, fetchOrganizationProductAnalyticsSucceeded, fetchOrganizationProductAnalyticsFailed, ProductAnalyticsResponse,
} from './redux/productAnalytics';
import {
  fetchOrganizationDetailsFailed, fetchOrganizationDetailsInitialized, fetchOrganizationDetailsSucceeded, OrganizationDetailsResponse,
} from './redux/details';

export const confirmReservationInit = createAction(
  '@platform/command/organization/confirmReservation/init',
);
export const confirmReservationSucceeded = createAction(
  '@platform/command/organization/confirmReservation/success',
  (payload: {
    set_password_code: string
  }) => payload,
);
export const confirmReservationFailed = createAction(
  '@platform/command/organization/confirmReservation/failed',
  (payload: string) => payload,
);

const generateKey = (...args: Array<string>): string => md5(JSON.stringify(args));

const generateUrl = (url: string, userId: string): string => {
  const hashFromUserId = generateKey(userId);

  return `${url}?cb=${hashFromUserId}`;
};

@Service()
export class Organization {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFailInterceptor: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('config') private readonly config: ConfigService,
  ) {}

  async fetchDetails(): Promise<OrganizationDetailsResponse> {
    this.store.dispatch(fetchOrganizationDetailsInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('ORGANIZATION_URL')}/current`,
        method: 'GET',
      })
        .json()
        .send<OrganizationDetailsResponse>();

      this.store.dispatch(fetchOrganizationDetailsSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(fetchOrganizationDetailsFailed());

      throw err;
    }
  }

  async fetchConfig(userId: string) {
    try {
      const resp = await this.httpClient.request({
        endpoint: generateUrl(
          `${this.config.get('ORGANIZATION_URL')}/product-bar`,
          userId,
        ),
        method: 'GET',
      })
        .intercept(this.notifyOnFailInterceptor)
        .jsonApi()
        .send<OrganizationConfigResponse>();

      this.store.dispatch(
        fetchOrganizationConfigSucceeded(resp),
      );

      return resp;
    } catch (err) {
      this.store.dispatch(fetchOrganizationConfigFailed());

      throw err;
    }
  }

  async checkReservation(reservationCode: string): Promise<{ set_password_code: string } | Error> {
    this.store.dispatch(confirmReservationInit());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('ORGANIZATION_URL')}/reservation/confirm`,
        method: 'POST',
        body: {
          reservation_code: reservationCode,
        },
      })
        .json()
        .send<{
        set_password_code: string
      }>();

      this.store.dispatch(confirmReservationSucceeded(resp));

      return resp;
    } catch (err: any) {
      this.store.dispatch(confirmReservationFailed(err));

      throw err;
    }
  }

  async fetchBranding() {
    this.store.dispatch(fetchOrganizationBrandingInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('ORGANIZATION_URL')}/logo`,
        method: 'GET',
      })
        .intercept(this.notifyOnFailInterceptor)
        .json()
        .send<BrandingResponse>();

      this.store.dispatch(fetchOrganizationBrandingSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(fetchOrganizationBrandingFailed());

      throw err;
    }
  }

  async resetBranding() {
    return this.confirmation.confirm({
      title: 'administration:accountSettings.logoAndFavicon.layout.settingsPreview.settings.restoreDefaultModal.title',
      messages: [
        'administration:accountSettings.logoAndFavicon.layout.settingsPreview.settings.restoreDefaultModal.message',
      ],
      applyBtnAppearance: 'primary',
      applyBtnText: 'administration:accountSettings.logoAndFavicon.layout.settingsPreview.settings.restoreDefaultModal.confirm',
      asyncAction: async () => {
        await this.httpClient.request({
          endpoint: `${this.config.get('ORGANIZATION_URL')}/logo`,
          method: 'DELETE',
        })
          .intercept(this.notifyAboutUpdate)
          .json()
          .send();
      },
    });
  }

  async sendBrandingImages({ logo, favicon }: { logo: File, favicon: File }) {
    const formData = new FormData();

    formData.append('logo', logo);
    formData.append('favicon', favicon);

    try {
      await this.httpClient.request({
        endpoint: `${this.config.get('ORGANIZATION_URL')}/logo`,
        method: 'POST',
        body: formData,
      })
        .intercept(this.notifyAboutUpdate)
        .file()
        .send();
    } catch (err: any) {
      throw err;
    }
  }

  async fetchProductAnalytics() {
    this.store.dispatch(fetchOrganizationProductAnalyticsInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: '/api/organization/v2/product-analytics',
        method: 'GET',
      })
        .json()
        .send<ProductAnalyticsResponse>();

      this.store.dispatch(fetchOrganizationProductAnalyticsSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(fetchOrganizationProductAnalyticsFailed());

      throw err;
    }
  }
}
